'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

interface BookingFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  sessionType: string;
  preferredDate: string;
  preferredTime: string;
  message?: string;
}

const sessionOptions = [
  { value: 'audio-15', label: 'Audio 15 min', price: '£9.50', duration: '15 min', mode: 'Audio' },
  { value: 'video-15', label: 'Vidéo Face to Face 15 min', price: '£17.50', duration: '15 min', mode: 'Vidéo' },
  { value: 'audio-45', label: 'Audio 45 min', price: '£55', duration: '45 min', mode: 'Audio' },
  { value: 'video-45', label: 'Vidéo 45 min', price: '£65', duration: '45 min', mode: 'Vidéo' },
  { value: 'city-immersion', label: 'Rendez-vous en ville — Total Immersion 1h', price: '£75', duration: '1h', mode: 'En ville' },
];

interface BookingConfirmation {
  orderRef: string;
  firstName: string;
  lastName: string;
  email: string;
  sessionLabel: string;
  sessionPrice: string;
  sessionDuration: string;
  sessionMode: string;
  preferredDate: string;
  preferredTime: string;
  checkoutUrl: string;
}

interface BookingFormProps {
  initialSessionType?: string;
}

function generateOrderRef(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let ref = 'AV-';
  for (let i = 0; i < 8; i++) {
    ref += chars[Math.floor(Math.random() * chars.length)];
  }
  return ref;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
}

export default function BookingForm({ initialSessionType }: BookingFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState<BookingConfirmation | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<BookingFormData>({
    defaultValues: { sessionType: initialSessionType ?? 'audio-45' },
  });

  const onSubmit = async (data: BookingFormData) => {
    setIsSubmitting(true);
    try {
      const res = await fetch('/api/stripe/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'session',
          sessionType: data.sessionType,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          preferredDate: data.preferredDate,
          preferredTime: data.preferredTime,
          phone: data.phone,
          message: data.message,
        }),
      });

      const json = await res.json();

      if (!res.ok || !json.url) {
        throw new Error(json.error ?? 'Erreur lors de la création du paiement');
      }

      const selectedSession = sessionOptions.find((s) => s.value === data.sessionType) ?? sessionOptions[2];

      setConfirmation({
        orderRef: generateOrderRef(),
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        sessionLabel: selectedSession.label,
        sessionPrice: selectedSession.price,
        sessionDuration: selectedSession.duration,
        sessionMode: selectedSession.mode,
        preferredDate: data.preferredDate,
        preferredTime: data.preferredTime,
        checkoutUrl: json.url,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Une erreur est survenue';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProceedToPayment = () => {
    if (!confirmation) return;
    setIsRedirecting(true);
    window.location.href = confirmation.checkoutUrl;
  };

  if (confirmation) {
    return (
      <section id="booking-form" className="py-16 lg:py-20 bg-muted/30">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl border border-border shadow-card p-8 lg:p-10">
              {/* Success header */}
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-extrabold text-primary">Demande reçue !</h2>
                  <p className="text-sm text-muted-foreground">Vérifiez vos informations avant de payer</p>
                </div>
              </div>

              {/* Order reference */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3.5 mb-6 flex items-center justify-between">
                <span className="text-sm font-semibold text-primary">Référence de commande</span>
                <span className="text-sm font-mono font-bold text-amber-700 tracking-widest">{confirmation.orderRef}</span>
              </div>

              {/* Session details */}
              <div className="rounded-xl border border-border overflow-hidden mb-6">
                <div className="bg-muted/40 px-5 py-3 border-b border-border">
                  <h3 className="text-sm font-bold text-primary uppercase tracking-wide">Détails de la séance</h3>
                </div>
                <div className="divide-y divide-border">
                  <div className="flex justify-between items-center px-5 py-3">
                    <span className="text-sm text-muted-foreground">Séance</span>
                    <span className="text-sm font-semibold text-primary text-right max-w-[60%]">{confirmation.sessionLabel}</span>
                  </div>
                  <div className="flex justify-between items-center px-5 py-3">
                    <span className="text-sm text-muted-foreground">Durée</span>
                    <span className="text-sm font-semibold text-primary">{confirmation.sessionDuration}</span>
                  </div>
                  <div className="flex justify-between items-center px-5 py-3">
                    <span className="text-sm text-muted-foreground">Format</span>
                    <span className="text-sm font-semibold text-primary">{confirmation.sessionMode}</span>
                  </div>
                  <div className="flex justify-between items-center px-5 py-3">
                    <span className="text-sm text-muted-foreground">Date souhaitée</span>
                    <span className="text-sm font-semibold text-primary">{formatDate(confirmation.preferredDate)}</span>
                  </div>
                  <div className="flex justify-between items-center px-5 py-3">
                    <span className="text-sm text-muted-foreground">Heure souhaitée</span>
                    <span className="text-sm font-semibold text-primary">{confirmation.preferredTime}</span>
                  </div>
                  <div className="flex justify-between items-center px-5 py-3">
                    <span className="text-sm text-muted-foreground">Nom</span>
                    <span className="text-sm font-semibold text-primary">{confirmation.firstName} {confirmation.lastName}</span>
                  </div>
                  <div className="flex justify-between items-center px-5 py-3">
                    <span className="text-sm text-muted-foreground">Email</span>
                    <span className="text-sm font-semibold text-primary">{confirmation.email}</span>
                  </div>
                  <div className="flex justify-between items-center px-5 py-3 bg-primary/5">
                    <span className="text-sm font-bold text-primary">Total à payer</span>
                    <span className="text-lg font-extrabold text-primary">{confirmation.sessionPrice}</span>
                  </div>
                </div>
              </div>

              {/* Next steps */}
              <div className="rounded-xl border border-blue-200 bg-blue-50 px-5 py-4 mb-6">
                <h3 className="text-sm font-bold text-primary mb-3">📋 Prochaines étapes</h3>
                <ol className="space-y-2">
                  <li className="flex items-start gap-2.5">
                    <span className="w-5 h-5 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                    <span className="text-sm text-secondary">Cliquez sur <strong>Procéder au paiement</strong> ci-dessous pour régler votre séance en toute sécurité via Stripe.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="w-5 h-5 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                    <span className="text-sm text-secondary">Une fois le paiement confirmé, vous recevrez un email de confirmation à <strong>{confirmation.email}</strong>.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="w-5 h-5 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                    <span className="text-sm text-secondary">Marc vous contactera pour confirmer la date et l&apos;heure de votre séance.</span>
                  </li>
                </ol>
              </div>

              {/* CTA */}
              <button
                onClick={handleProceedToPayment}
                disabled={isRedirecting}
                className="w-full py-3.5 btn-primary rounded-xl text-base font-semibold disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ minHeight: '52px' }}
              >
                {isRedirecting ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Redirection vers le paiement...
                  </>
                ) : (
                  <>🔒 Procéder au paiement — {confirmation.sessionPrice}</>
                )}
              </button>
              <p className="text-xs text-center text-muted-foreground mt-3">
                🔒 Paiement sécurisé par Stripe. Référence : <span className="font-mono font-semibold">{confirmation.orderRef}</span>
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="booking-form" className="py-16 lg:py-20 bg-muted/30">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl border border-border shadow-card p-8 lg:p-10">
            <h2 className="text-2xl font-extrabold text-primary mb-1">Réserver votre séance</h2>
            <p className="text-sm text-muted-foreground mb-8">
              Tous les champs marqués <span className="text-error">*</span> sont obligatoires.
            </p>

            {/* Free lesson callout */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8 flex items-start gap-3">
              <span className="text-xl flex-shrink-0">🎁</span>
              <div>
                <p className="text-sm font-semibold text-primary">Première séance offerte</p>
                <p className="text-xs text-secondary mt-0.5">
                  Mentionnez <strong>&ldquo;PREMIÈRE FOIS&rdquo;</strong> dans votre message pour bénéficier de votre première séance gratuite.
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
              {/* Name row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-primary mb-1.5">
                    Prénom <span className="text-error">*</span>
                  </label>
                  <input
                    {...register('firstName', { required: 'Prénom requis' })}
                    className="input-field"
                    placeholder="Marie"
                    type="text"
                    autoComplete="given-name"
                  />
                  {errors.firstName && (
                    <p className="text-xs text-error mt-1">{errors.firstName.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-primary mb-1.5">
                    Nom <span className="text-error">*</span>
                  </label>
                  <input
                    {...register('lastName', { required: 'Nom requis' })}
                    className="input-field"
                    placeholder="Dupont"
                    type="text"
                    autoComplete="family-name"
                  />
                  {errors.lastName && (
                    <p className="text-xs text-error mt-1">{errors.lastName.message}</p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-primary mb-1.5">
                  Adresse email <span className="text-error">*</span>
                </label>
                <input
                  {...register('email', {
                    required: 'Email requis',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email invalide' },
                  })}
                  className="input-field"
                  placeholder="marie.dupont@email.fr"
                  type="email"
                  autoComplete="email"
                />
                {errors.email && (
                  <p className="text-xs text-error mt-1">{errors.email.message}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-semibold text-primary mb-1.5">
                  Téléphone
                  <span className="text-muted-foreground font-normal ml-1">(optionnel)</span>
                </label>
                <p className="text-xs text-muted-foreground mb-1.5">Pour vous contacter si besoin avant la séance</p>
                <input
                  {...register('phone')}
                  className="input-field"
                  placeholder="+33 6 12 34 56 78"
                  type="tel"
                  autoComplete="tel"
                />
              </div>

              {/* Session type */}
              <div>
                <label className="block text-sm font-semibold text-primary mb-1.5">
                  Type de séance <span className="text-error">*</span>
                </label>
                <select
                  {...register('sessionType', { required: 'Choisissez une séance' })}
                  className="input-field"
                >
                  {sessionOptions.map((opt) => (
                    <option key={`session-opt-${opt.value}`} value={opt.value}>
                      {opt.label} — {opt.price}
                    </option>
                  ))}
                </select>
                {errors.sessionType && (
                  <p className="text-xs text-error mt-1">{errors.sessionType.message}</p>
                )}
              </div>

              {/* Date + Time */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-primary mb-1.5">
                    Date souhaitée <span className="text-error">*</span>
                  </label>
                  <input
                    {...register('preferredDate', { required: 'Date requise' })}
                    className="input-field"
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                  />
                  {errors.preferredDate && (
                    <p className="text-xs text-error mt-1">{errors.preferredDate.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-primary mb-1.5">
                    Heure souhaitée <span className="text-error">*</span>
                  </label>
                  <input
                    {...register('preferredTime', { required: 'Heure requise' })}
                    className="input-field"
                    type="time"
                  />
                  {errors.preferredTime && (
                    <p className="text-xs text-error mt-1">{errors.preferredTime.message}</p>
                  )}
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-semibold text-primary mb-1.5">
                  Message / Notes
                  <span className="text-muted-foreground font-normal ml-1">(optionnel)</span>
                </label>
                <p className="text-xs text-muted-foreground mb-1.5">
                  Mentionnez votre niveau, vos objectifs, ou &ldquo;PREMIÈRE FOIS&rdquo; pour la séance gratuite
                </p>
                <textarea
                  {...register('message')}
                  className="input-field resize-none"
                  rows={4}
                  placeholder="Ex: Je suis débutant, je veux travailler sur les conversations professionnelles. PREMIÈRE FOIS."
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3.5 btn-primary rounded-xl text-base font-semibold disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ minHeight: '52px' }}
              >
                {isSubmitting ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Traitement en cours...
                  </>
                ) : (
                  <>🔒 Confirmer et payer</>
                )}
              </button>

              <p className="text-xs text-center text-muted-foreground">
                🔒 Paiement sécurisé par Stripe. Vous serez redirigé vers la page de paiement.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}